import React from 'react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Shield, CheckCircle, XCircle, Search, FileText, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api from '@/lib/api';

export default function VerifyPage() {
  const [codigo, setCodigo] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [searched, setSearched] = React.useState(false);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!codigo.trim()) {
      toast.error('Digite o código de verificação');
      return;
    }

    setLoading(true);
    setSearched(true);
    try {
      const response = await api.post('/verificar', { codigo: codigo.trim() });
      setResult(response.data);
    } catch (error) {
      toast.error('Erro ao verificar código');
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="verify-page">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-[#B91C1C]/10 rounded-full mb-6">
            <Shield className="text-[#B91C1C]" size={40} />
          </div>
          <h1 className="font-heading text-3xl font-bold mb-4 dark:text-white">Verificação de Autenticidade</h1>
          <p className="text-gray-600 dark:text-gray-400 max-w-lg mx-auto">
            Consulte a veracidade das publicações do Diário Oficial Eletrônico Municipal de Acaiaca 
            através do código de verificação presente no documento.
          </p>
        </div>

        {/* Search Form */}
        <Card className="mb-8 dark:bg-[#374151]">
          <CardContent className="pt-6">
            <form onSubmit={handleVerify} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Código de Verificação
                </label>
                <div className="flex gap-3">
                  <Input
                    placeholder="Ex: ED-XXXXXXXXXXXX ou PUB-XXXXXXXXXXXX"
                    value={codigo}
                    onChange={(e) => setCodigo(e.target.value.toUpperCase())}
                    className="flex-1 font-mono dark:bg-[#1F2937] dark:border-gray-600"
                    data-testid="verify-input"
                  />
                  <Button 
                    type="submit"
                    className="bg-[#B91C1C] hover:bg-[#991B1B]"
                    disabled={loading}
                    data-testid="verify-btn"
                  >
                    <Search size={18} className="mr-2" />
                    {loading ? 'Verificando...' : 'Verificar'}
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Result */}
        {searched && (
          <Card className={`border-2 ${result?.valido ? 'border-green-500' : 'border-red-500'} dark:bg-[#374151]`} data-testid="verify-result">
            <CardContent className="pt-6">
              {result?.valido ? (
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full mb-4">
                    <CheckCircle className="text-green-600 dark:text-green-400" size={32} />
                  </div>
                  <h2 className="font-heading text-2xl font-bold text-green-700 dark:text-green-400 mb-2">
                    Documento Autêntico
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Este {result.tipo === 'edicao' ? 'edição' : 'publicação'} é oficial e foi verificado(a) com sucesso.
                  </p>

                  {result.detalhes && (
                    <div className="bg-gray-50 dark:bg-[#1F2937] rounded-lg p-6 text-left mb-6">
                      <h3 className="font-semibold mb-4 text-gray-800 dark:text-white">Detalhes do Documento</h3>
                      
                      {result.tipo === 'edicao' ? (
                        <dl className="space-y-3">
                          <div className="flex justify-between">
                            <dt className="text-gray-600 dark:text-gray-400">Edição:</dt>
                            <dd className="font-semibold dark:text-white">Nº {result.detalhes.numero_edicao}/{result.detalhes.ano}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-gray-600 dark:text-gray-400">Data de Publicação:</dt>
                            <dd className="font-semibold dark:text-white">{formatDate(result.detalhes.data_publicacao)}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-gray-600 dark:text-gray-400">Status:</dt>
                            <dd>
                              <Badge className={result.detalhes.status === 'publicado' ? 'bg-green-600' : 'bg-yellow-600'}>
                                {result.detalhes.status}
                              </Badge>
                            </dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-gray-600 dark:text-gray-400">Total de Publicações:</dt>
                            <dd className="font-semibold dark:text-white">{result.detalhes.total_publicacoes}</dd>
                          </div>
                        </dl>
                      ) : (
                        <dl className="space-y-3">
                          <div className="flex justify-between">
                            <dt className="text-gray-600 dark:text-gray-400">Título:</dt>
                            <dd className="font-semibold text-right dark:text-white">{result.detalhes.titulo}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-gray-600 dark:text-gray-400">Categoria:</dt>
                            <dd>
                              <Badge className="bg-[#B91C1C]">{result.detalhes.categoria}</Badge>
                            </dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-gray-600 dark:text-gray-400">Tipo:</dt>
                            <dd className="font-semibold dark:text-white">{result.detalhes.subcategoria}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-gray-600 dark:text-gray-400">Edição:</dt>
                            <dd className="font-semibold dark:text-white">Nº {result.detalhes.numero_edicao}/{result.detalhes.ano}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-gray-600 dark:text-gray-400">Data de Publicação:</dt>
                            <dd className="font-semibold dark:text-white">{formatDate(result.detalhes.data_publicacao)}</dd>
                          </div>
                        </dl>
                      )}
                    </div>
                  )}

                  {/* Digital Signature Info */}
                  {result.assinatura_digital?.assinado && (
                    <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-6 text-left border border-blue-200 dark:border-blue-800">
                      <h3 className="font-semibold mb-4 text-blue-800 dark:text-blue-300 flex items-center gap-2">
                        <Lock size={18} />
                        Assinatura Digital
                      </h3>
                      <dl className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <dt className="text-blue-600 dark:text-blue-400">Assinante:</dt>
                          <dd className="font-semibold text-blue-800 dark:text-blue-200">{result.assinatura_digital.assinante}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-blue-600 dark:text-blue-400">Data:</dt>
                          <dd className="font-semibold text-blue-800 dark:text-blue-200">
                            {result.assinatura_digital.data_assinatura?.slice(0, 10)}
                          </dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-blue-600 dark:text-blue-400">Hash:</dt>
                          <dd className="font-mono text-xs text-blue-800 dark:text-blue-200">{result.assinatura_digital.hash}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-blue-600 dark:text-blue-400">Algoritmo:</dt>
                          <dd className="font-semibold text-blue-800 dark:text-blue-200">{result.assinatura_digital.algoritmo}</dd>
                        </div>
                      </dl>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 dark:bg-red-900 rounded-full mb-4">
                    <XCircle className="text-red-600 dark:text-red-400" size={32} />
                  </div>
                  <h2 className="font-heading text-2xl font-bold text-red-700 dark:text-red-400 mb-2">
                    Código Não Encontrado
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400">
                    O código informado não corresponde a nenhum documento oficial registrado no sistema.
                    Verifique se o código foi digitado corretamente.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Instructions */}
        <Card className="mt-8 bg-gray-50 dark:bg-[#374151]">
          <CardContent className="pt-6">
            <h3 className="font-heading font-semibold mb-4 flex items-center gap-2 dark:text-white">
              <FileText size={20} className="text-[#B91C1C]" />
              Como encontrar o código de verificação?
            </h3>
            <ul className="space-y-2 text-gray-600 dark:text-gray-400 text-sm">
              <li className="flex items-start gap-2">
                <span className="text-[#B91C1C] font-bold">•</span>
                O código de verificação está presente no cabeçalho ou rodapé do documento PDF
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#B91C1C] font-bold">•</span>
                Códigos de edição começam com <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">ED-</code>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#B91C1C] font-bold">•</span>
                Códigos de publicação começam com <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">PUB-</code>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#B91C1C] font-bold">•</span>
                Digite o código completo incluindo o prefixo e os hífens
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#B91C1C] font-bold">•</span>
                Documentos assinados digitalmente mostram informações da assinatura
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
